<template>
  <BTable
    v-bind="$attrs"
    :selected="value"
    :headers="headers"
    :actions="computedActions"
    :list="list"
    :full-width-actions="false"
    dense
    v-on="$listeners || {}"
  >
    <!--  -->

    <template #item.username="{ header, data }">
      <span v-html="header.field(data)" />
    </template>

    <template #item.is_submitted="{ header, data }">
      <v-tooltip
        v-if="!data.first_level_expert_one_id && !data.first_level_expert_two_id && !data.second_level_expert_id
          && !!data.status && ['submitted'].includes(data.status)"
        top
      >
        <template #activator="{ on }">
          <span>{{ header.field(data.status, data.is_submitted) }}
            <v-icon
              color="error"
              size="16px"
              class="mr-2"
              v-on="on"
              v-text="'mdi-alert-circle-outline'"
            />
          </span>
        </template>
        <slot>
          <span>Эксперты не назначены</span>
        </slot>
      </v-tooltip>
      <span v-else>{{ header.field(data.status, data.is_submitted) }}</span>
    </template>

    <template #item.first_level_expert_one_id="{ data, index }">
      <LKMunMehExpertSelector
        v-model="data.first_level_expert_one_id"
        :data="data"
        :loading="isLoading"
        :experts="experts"
        :expert-index="1"
        :row-index="index"
        :curator-id="data.curator_id"

        v-on="$listeners"
      />
    </template>

    <template #item.first_level_expert_two_id="{ data, index }">
      <LKMunMehExpertSelector
        v-model="data.first_level_expert_two_id"
        :data="data"
        :loading="isLoading"
        :experts="experts"
        :expert-index="2"
        :row-index="index"
        :curator-id="data.curator_id"

        v-on="$listeners"
      />
    </template>

    <template #item.second_level_expert_id="{ data, index }">
      <LKMunMehExpertSelector
        v-model="data.second_level_expert_id"
        :data="data"
        :loading="isLoading"
        :experts="experts"
        :expert-index="3"
        :row-index="index"
        :curator-id="data.curator_id"

        v-on="$listeners"
      />
    </template>

    <template #item.reports="{ header, data }">
      <BTableButtonList
        v-if="header.getFilter(data) && isReportsButtonVisible"
        :list="reportTypes"
        label="Выберите отчет"
        @action:click="(arg1, arg2) => onDownloadReport(arg1, arg2, data)"
      />
    </template>

    <template #item.actions.finish="{ action, item, index, data }">
      <BTableButtonAction
        v-if="isFinishedButtonVisible(item)"
        :key="`${action.action}.${index}`"
        :action="action"
        :index="index"
        :data="data"
        :list="list"
        @action="onFinish"
      />
    </template>

    <template #item.actions.admin="{ action, data }">
      <BTableButtonList
        :list="adminStatusesList"
        :label="action.tooltip"
        @action:click="(arg1, arg2) => onManipulateForm(arg1, arg2, data)"
      />
    </template>


    <!--  -->
  </BTable>
</template>

<script>
import { SUPER_ADMIN_ACTION, TABLE_ACTIONS, TABLE_HEADERS } from '@/views/LKMunMeh/Form/table-headers'
import BTableSimple from './BTableSimple.vue'
import BTableButtonAction from '@/components/common/BTable/BTableButtonAction.vue'
import BTableButtonList from '@/components/tables/BTableCommon/components/BTableButtonList.vue'
import { LkMunMehAdapter } from '@/views/LKMunMeh/store/LkMunMehAdapter'
import { userNameInitials } from '@/helpers/nameFormatter'
import LKMunMehExpertSelector from '@/views/LKMunMeh/components/FormsListModal/LKMunMehExpertSelector.vue'
import { rootMapper } from '@/store/modules/root'
import { LkMunMehApi, LkReportsApi } from '@/api/resources'
import { downloadLinkByGet } from '@/helpers/downloadBlob'
import { getSingleReportsList } from '@/views/LKMunMeh/components/FormsListModal/utils'
import { MunMehFormStatuses } from '@/typings/api/MunMeh'

const ADMIN_STATUSES = [
  {
    text: 'В работе',
    tooltip: null,
    value: MunMehFormStatuses.in_progress,
  },
  {
    text: 'Сдано',
    tooltip: 'Перевести на оценку экспертам первого уровня',
    value: MunMehFormStatuses.submitted,
  },
  {
    text: 'Оценили эксперты 1 уровня',
    tooltip: 'Перевести на оценку эксперту второго уровня',
    value: MunMehFormStatuses.first_level_marked,
  },
  {
    text: 'Оценил эксперт 2 уровня',
    tooltip: 'Завершить оценку экспертов',
    value: MunMehFormStatuses.second_level_marked,
  },
]


export default {
  name: 'BTableMunMehCuratorForms',
  components: { LKMunMehExpertSelector, BTableButtonAction, BTableButtonList },
  extends: BTableSimple,
  props: {
    value: Array,
    headers: {
      type: Array,
      default: () => TABLE_HEADERS,
    },
    actions: {
      type: Array,
      default: () => TABLE_ACTIONS,
    },
    munMehId: {
      type: Number,
      required: true,
    },
    regMeh: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    isLoading: false,
    experts: [],
    adminStatusesList: ADMIN_STATUSES,
  }),
  computed: {
    ...rootMapper.mapGetters(['profile']),
    reportTypes() {
      return getSingleReportsList(this.regMeh)
    },
    isReportsButtonVisible() {
      return this.profile.is_admin || this.profile.is_superuser || this.profile.is_federal_level
    },
    computedActions() {
      if (this.profile.is_superuser) {
        return this.actions.concat([SUPER_ADMIN_ACTION])
      }

      return this.actions
    },
  },
  created() {
    this.api = new LkMunMehApi()
    this.lkReportsApi = new LkReportsApi()
  },
  methods: {
    async asyncData() {
      this.isLoading = true
      this.adapter = new LkMunMehAdapter()

      try {
        const { data } = await this.adapter.api.getExperts(this.munMehId)

        const dataExpertsIds = [
          ...new Set(
            this.list
              .map((el) => ([el.first_level_expert_one_id, el.first_level_expert_two_id, el.second_level_expert_id]))
              .flat(),
          ),
        ]
        const unknownExperts = dataExpertsIds
          .filter((id) => data.findIndex((el) => el.id === id) < 0 && !!id)
          .map((id) => ({ id, full_name: id, unknown: true }))

        const experts = data.map((el) => ({
          ...el,
          full_name: `${userNameInitials(el.full_name)} | ${el.username}`,
        }))

        this.experts = [].concat(experts, unknownExperts)
      } catch (e) {
        this.$notice.errors(e)
      }
      this.isLoading = false
    },
    async onManipulateForm(form, callback, data) {
      const unwatch = callback?.()

      try {
        await this.adapter.api.openFormById(data.id, {
          status: form.value,
        })
        // eslint-disable-next-line no-param-reassign
        data.status = form.value
      } catch (e) {
        this.$notice.errors(e)
      }
      unwatch?.()
    },
    async onDownloadReport(report, callback, data) {
      const unwatch = callback?.()

      try {
        const { id } = await this.api.getMenMehFormReport(data.id, {
          code: report.value,
        })

        const link = this.lkReportsApi.getDownloadUrl(id)
        await downloadLinkByGet(link)
      } catch (e) {
        this.$notice.errors(e)
      }

      unwatch?.()
    },
    async onFinish(action, data, callback) {
      const unwatch = callback?.()

      try {
        await this.adapter.api.finish(data.id)
        // eslint-disable-next-line no-param-reassign
        data.status = MunMehFormStatuses.finished
      } catch (e) {
        this.$notice.errors(e)
      }

      unwatch?.()
    },
    isFinishedButtonVisible(item) {
      return (this.profile.is_superuser || this.profile.is_federal_level || this.profile.id === item.curator_id)
    },
  },
}
</script>

<style lang="scss">
.red--text {
  color: $webflow-color-red;
}

.b-autocomplete {
  &.v-text-field.v-text-field--enclosed .v-text-field__details {
    padding: 0 2px;
    margin-bottom: 0;
    font-size: 10px;
  }

  .v-chip--disabled {
    opacity: 1;
  }

  &__error-icon {
    @include animation(shake, 4px);
  }
}
</style>
